import React, { useState, useEffect } from 'react';
import { Save, Plus, X, AlertCircle } from 'lucide-react';
import { getPositionGroups } from '../../data/positionHierarchy';
import type { Sport } from '../../types';
import { supabase } from '../../lib/supabase';
import type { FormationPosition } from './VisualFormationBuilder';

interface PositionMapping {
  id?: string;
  position_label: string;
  position_group_key: string;
}

interface PositionMappingEditorProps {
  teamId: string;
  sport: Sport;
  unit: 'Offense' | 'Defense' | 'Special Teams';
  formationId?: string;
  formationName?: string;
  positions: FormationPosition[];
  onSave?: () => void;
}

const PositionMappingEditor: React.FC<PositionMappingEditorProps> = ({
  teamId,
  sport,
  unit,
  formationId,
  formationName,
  positions,
  onSave
}) => {
  const [mappings, setMappings] = useState<Record<string, string[]>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const positionGroups = getPositionGroups(sport, unit);

  // Load existing mappings
  useEffect(() => {
    loadMappings();
  }, [teamId, formationId, formationName, sport, unit]);

  const loadMappings = async () => {
    try {
      setIsLoading(true);
      let query = supabase
        .from('position_mappings')
        .select('*')
        .eq('team_id', teamId)
        .eq('sport', sport)
        .eq('unit', unit);

      if (formationId) {
        query = query.eq('formation_id', formationId);
      } else if (formationName) {
        query = query.eq('formation_name', formationName);
      } else {
        // Global mappings (no formation specified)
        query = query.is('formation_id', null).is('formation_name', null);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Group mappings by position label
      const grouped: Record<string, string[]> = {};
      (data || []).forEach((mapping: any) => {
        if (!grouped[mapping.position_label]) {
          grouped[mapping.position_label] = [];
        }
        grouped[mapping.position_label].push(mapping.position_group_key);
      });

      setMappings(grouped);
    } catch (error) {
      console.error('Error loading position mappings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Add mapping for a position label
  const addMapping = (positionLabel: string, positionGroupKey: string) => {
    if (!mappings[positionLabel]) {
      setMappings({ ...mappings, [positionLabel]: [] });
    }
    if (!mappings[positionLabel]?.includes(positionGroupKey)) {
      setMappings({
        ...mappings,
        [positionLabel]: [...(mappings[positionLabel] || []), positionGroupKey],
      });
    }
  };

  // Remove mapping for a position label
  const removeMapping = (positionLabel: string, positionGroupKey: string) => {
    setMappings({
      ...mappings,
      [positionLabel]: (mappings[positionLabel] || []).filter(
        (key) => key !== positionGroupKey
      ),
    });
  };

  // Save all mappings
  const handleSave = async () => {
    // Validate all positions have at least one mapping
    const unmappedLabels = positionLabels.filter(label => {
      const mappings = mappings[label] || [];
      return mappings.length === 0;
    });

    if (unmappedLabels.length > 0) {
      alert(`Please map all positions to position groups. ${unmappedLabels.length} position(s) are missing mappings:\n${unmappedLabels.join(', ')}`);
      return;
    }

    try {
      setIsSaving(true);

      // Delete existing mappings for this formation
      let deleteQuery = supabase
        .from('position_mappings')
        .delete()
        .eq('team_id', teamId)
        .eq('sport', sport)
        .eq('unit', unit);

      if (formationId) {
        deleteQuery = deleteQuery.eq('formation_id', formationId);
      } else if (formationName) {
        deleteQuery = deleteQuery.eq('formation_name', formationName);
      } else {
        deleteQuery = deleteQuery.is('formation_id', null).is('formation_name', null);
      }

      await deleteQuery;

      // Insert new mappings
      const inserts: any[] = [];
      Object.entries(mappings).forEach(([positionLabel, positionGroupKeys]) => {
        positionGroupKeys.forEach((positionGroupKey) => {
          inserts.push({
            team_id: teamId,
            formation_id: formationId || null,
            formation_name: formationName || null,
            position_label: positionLabel,
            position_group_key: positionGroupKey,
            sport,
            unit,
          });
        });
      });

      if (inserts.length > 0) {
        const { error } = await supabase
          .from('position_mappings')
          .insert(inserts);

        if (error) throw error;
      }

      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error('Error saving position mappings:', error);
      alert('Failed to save position mappings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Get unique position labels from positions
  const positionLabels = Array.from(
    new Set(positions.filter(p => p.label.trim()).map(p => p.label.trim()))
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="loading loading-spinner loading-md"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Position Mappings</h3>
          <p className="text-sm text-gray-600 mt-1">
            Map your custom position labels to position groups so the system knows which players can play each position
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="btn btn-primary btn-sm flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {isSaving ? 'Saving...' : 'Save Mappings'}
        </button>
      </div>

      {positionLabels.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <AlertCircle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>No position labels found</p>
          <p className="text-sm mt-1">Add positions to your formation first</p>
        </div>
      ) : (
        <div className="space-y-4">
          {positionLabels.map((positionLabel) => {
            const currentMappings = mappings[positionLabel] || [];
            const position = positions.find(p => p.label.trim() === positionLabel);
            
            return (
              <div
                key={positionLabel}
                className="border rounded-lg p-4 bg-white"
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold">{positionLabel}</h4>
                    {position?.fullName && (
                      <p className="text-sm text-gray-600">{position.fullName}</p>
                    )}
                  </div>
                  {position?.positionGroupKey && (
                    <span className="badge badge-primary badge-sm">
                      {positionGroups.find(g => g.key === position.positionGroupKey)?.name || position.positionGroupKey}
                    </span>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="label">
                    <span className="label-text text-sm">
                      Which position groups can play this position?
                    </span>
                  </label>
                  
                  {/* Current mappings */}
                  {currentMappings.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {currentMappings.map((groupKey) => {
                        const group = positionGroups.find(g => g.key === groupKey);
                        return (
                          <div
                            key={groupKey}
                            className="badge badge-primary badge-lg gap-2"
                          >
                            {group?.name || groupKey}
                            <button
                              onClick={() => removeMapping(positionLabel, groupKey)}
                              className="btn btn-ghost btn-xs btn-circle"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Add mapping dropdown */}
                  <select
                    className="select select-bordered select-sm w-full"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        addMapping(positionLabel, e.target.value);
                        e.target.value = '';
                      }
                    }}
                  >
                    <option value="">Add position group...</option>
                    {positionGroups
                      .filter((group) => !currentMappings.includes(group.key))
                      .map((group) => (
                        <option key={group.key} value={group.key}>
                          {group.name}
                        </option>
                      ))}
                  </select>

                  {currentMappings.length === 0 && (
                    <p className="text-xs text-red-600 mt-1 font-medium">
                      ⚠️ Required: Please map at least one position group. Players won't be filtered without a mapping.
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-800">
          <strong>How it works:</strong> When you map a position label (like "DAWG") to a position group (like "RB"), 
          any player who has "RB" in their position groups can be assigned to the "DAWG" position in your formation.
        </p>
      </div>
    </div>
  );
};

export default PositionMappingEditor;

